import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, AccordionSummary, Autocomplete, Box, Button, ButtonGroup, Divider, TextField, ToggleButton, Typography } from "@mui/material";
import Grid from '@mui/material/Unstable_Grid2';
import * as React from 'react';
import { FileUploader } from "react-drag-drop-files";
import { trackDocumentChoicesPanelClick, trackDocumentToggle } from './analytics.js';
import EditText from './EditText/EditText.js';
import { reportError } from './ErrorReporter';
import { getJSONPathCompatibleKey } from './PathUtils.js';

// Keep in sync with backend/llm_processor.js PERSONAL_INJURY_CASE_TYPES
const caseTypes = [
    { label: 'Car Accident' },
    { label: 'Motor Vehicle Accident' },
    { label: 'Automobile Accident' },
    { label: 'MVA' },
    { label: 'Truck Accident' },
    { label: 'Motorcycle Accident' },
    { label: 'Bicycle Accident' },
    { label: 'Pedestrian accident' },
    { label: 'Boating Accident' },
    { label: 'Bus Accident' },
    { label: 'Auto Accident' },
    { label: 'Train Accident' },
    { label: 'Aircraft Accident' },
    { label: 'Aviation Accident' },
    { label: 'Recreational Vehicle Accident' },
    { label: 'Injuries from DUI' },
    { label: 'Traffic Accident' },
    { label: 'Motorcycle Insurance Claims' },
    { label: 'Insurance Claims' },
    { label: 'Slip and fall' },
    { label: 'Dog bite' },
    { label: 'Animal Attack' },
    { label: 'Swimming Pool Accident' },
    { label: 'Elevator Accident' },
    { label: 'Escalator Accident' },
    { label: 'Inadequate Security' },
    { label: 'Negligent Security' },
    { label: 'Fire and Burn Injury' },
    { label: 'Toxic Substances Exposure' },
    { label: 'Worker\'s Compensation' },
    { label: 'Construction Accident' },
    { label: 'Construction Injury' },
    { label: 'Industrial Accident' },
    { label: 'Medical Malpractice' },
    { label: 'Medical Negligence' },
    { label: 'Misdiagnosis or Delayed Diagnosis' },
    { label: 'Surgical Errors' },
    { label: 'Medication & Prescription Errors' },
    { label: 'Anesthesia Errors' },
    { label: 'Birth Injuries' },
    { label: 'Nursing Home Abuse and Neglect' },
    { label: 'Pharmaceutical Injury' },
    { label: 'Pain and Suffering' },
    { label: 'Catastrophic Injury' },
    { label: 'Wrongful Death' },
    { label: 'Defective Product' },
    { label: 'Dangerous Drugs' },
    { label: 'Defective Medical Device' },
    { label: 'Sports and Recreational Accident' },
    { label: 'Product Liability' },
    { label: 'School and Playground Injury' },
    { label: 'Child Injury' },
    { label: 'Food Poisoning' },
    { label: 'Assault' },
    { label: 'Battery' },
    { label: 'Class Action Lawsuit' },
    { label: 'Brain Injury' },
    { label: 'Traumatic Brain Injury' },
    { label: 'Spinal Cord Injury' },
    { label: 'Severe Burn Injury' },
    { label: 'Railroad Accident' },
    { label: 'Maritime and Jones Act' },
    { label: 'Personal injury' },
];
caseTypes.sort((a, b) => a.label.localeCompare(b.label));

// Category header + one full document button + half of the second (margin:1 toggle buttons)
const DOCUMENT_CHOICES_PARTIAL_HEIGHT = 116;

function shouldUseCollapsibleDocumentPanel(categoryToSourceFilekeysMap) {
    const categories = Object.keys(categoryToSourceFilekeysMap);
    if (categories.length > 4) {
        return true;
    }
    return categories.some((category) => categoryToSourceFilekeysMap[category].length > 2);
}

function getDefaultDocumentChoicesView(caseHasDocuments, categoryToSourceFilekeysMap) {
    if (!caseHasDocuments) {
        return 'full';
    }
    return shouldUseCollapsibleDocumentPanel(categoryToSourceFilekeysMap) ? 'partial' : 'full';
}

export default function CaseSummaryHeader({
    caseInfo,
    setCaseInfo,
    refFileNamesToFilePropertiesMap,
    setFileNamesToFilePropertiesMap,
    uploadingFileNamesSet,
    onFilesSelectedForUpload,
    setSnackBarMessage,
    subscriptionOk = true,
    onSubscriptionRequired }) {

    const numTotalFiles = Object.keys(refFileNamesToFilePropertiesMap.current).length;
    var numSelectedFiles = Object.keys(refFileNamesToFilePropertiesMap.current).filter(filename => refFileNamesToFilePropertiesMap.current[filename]['selected']).length;
    // console.log('Initializing numSelectedFiles' + JSON.stringify(refFileNamesToFilePropertiesMap.current));

    let categoryToSourceFilekeysMap = {};
    for (let filekey in caseInfo['documents']) {
        let category = caseInfo['documents'][filekey].analysis?.overview?.length > 0 &&
            caseInfo['documents'][filekey].analysis.overview[0].category?.length > 0 ?
            caseInfo['documents'][filekey].analysis.overview[0].category :
            (caseInfo['documents'][filekey].status?.includes('processing') ? 'Processing' : 'Unknown');

        if (!(category in categoryToSourceFilekeysMap)) {
            categoryToSourceFilekeysMap[category] = [];
        }
        categoryToSourceFilekeysMap[category].push(filekey);
    }
    // These files are not in DB yet, but are being uploaded
    const uploadingFileNames = Object.keys(uploadingFileNamesSet).filter(filename => !(getJSONPathCompatibleKey(filename) in caseInfo['documents']));
    if (uploadingFileNames.length > 0) {
        if (!('Processing' in categoryToSourceFilekeysMap)) {
            categoryToSourceFilekeysMap['Processing'] = [];
        }
        categoryToSourceFilekeysMap['Processing'].push(...uploadingFileNames);
        uploadingFileNames.forEach(filename => {
            // temporarily add to caseInfo['documents'] to show in UI
            caseInfo['documents'][getJSONPathCompatibleKey(filename)] = {
                filename: getJSONPathCompatibleKey(filename),
                status: 'processing_ocr'
            };
        });
    }

    const [expanded, setExpanded] = React.useState(true);
    const onAccordianIconClicked = (prev) => { setExpanded(!expanded); }

    var caseHasDocuments = Object.keys(caseInfo['documents']).length > 0;
    const showCaseTypeWarning = !caseInfo['case_type'] && caseHasDocuments;
    const useCollapsibleDocumentPanel = shouldUseCollapsibleDocumentPanel(categoryToSourceFilekeysMap);
    const [documentChoicesView, setDocumentChoicesView] = React.useState(
        () => getDefaultDocumentChoicesView(caseHasDocuments, categoryToSourceFilekeysMap)
    );

    React.useEffect(() => {
        setDocumentChoicesView(getDefaultDocumentChoicesView(caseHasDocuments, categoryToSourceFilekeysMap));
    }, [caseInfo['caseId']]);

    const gridItems = Object.keys(categoryToSourceFilekeysMap).map(category =>
        <Grid xs={1} key={category}>
            {renderFileTogglesForCategory(category, categoryToSourceFilekeysMap, refFileNamesToFilePropertiesMap.current)}
        </Grid>
    );

    return (
        <div>
            <Accordion
                expanded={expanded || !caseHasDocuments}
                sx={{
                    backgroundColor: 'var(--background-color)',
                    border: '1px solid var(--border-color)',
                    boxShadow: 'var(--shadow-light)',
                    overflow: 'hidden',
                    mb: 3
                }}
            >
                <AccordionSummary
                    aria-controls="case-summary-header"
                    id="panel1d-header"
                    // Make only icon clickable to expand/contract Accordian from https://stackoverflow.com/questions/69358781/material-ui-expand-accordion-by-clicking-the-icon-only
                    sx={{
                        pointerEvents: "none",
                        backgroundColor: 'var(--surface-color)',
                        borderBottom: '1px solid var(--border-color)',
                        minHeight: '48px',
                        padding: '8px 16px',
                        alignItems: 'center',
                        '& .MuiAccordionSummary-content': {
                            alignItems: 'center'
                        },
                        '&:hover': {
                            backgroundColor: 'var(--surface-color)'
                        },
                        '&.Mui-focusVisible': {
                            backgroundColor: 'var(--surface-color)'
                        }
                    }}
                    expandIcon={<ExpandMoreIcon
                        sx={{
                            pointerEvents: "auto",
                            color: 'var(--text-secondary)',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                                color: 'var(--accent-color)',
                                transform: 'scale(1.1)'
                            }
                        }}
                        onClick={onAccordianIconClicked}
                    />}
                    style={{
                        flexDirection: "row-reverse",
                        cursor: "default",
                    }}
                >
                    <EditText
                        showEditButton={caseInfo['name'] && caseInfo['name'].startsWith('New Case')}
                        onSave={onCaseNameChanged}
                        sx={{
                            pointerEvents: "auto"
                        }}
                        style={{
                            fontSize: "1.25rem",
                            lineHeight: 1.6,
                            fontWeight: 500,
                            pointerEvents: "auto",
                            color: 'var(--text-primary)'
                        }}
                        defaultValue={caseInfo ? caseInfo['name'] || caseInfo['caseId'] : ''}
                    />
                    <Typography
                        display={caseHasDocuments ? "block" : "none"}
                        sx={{
                            color: 'var(--text-secondary)',
                            fontSize: '0.85rem',
                            fontWeight: 400
                        }}
                    >
                        &nbsp;&nbsp; Showing information from {numSelectedFiles} selected documents below (out of {numTotalFiles} uploaded)
                    </Typography>
                    <ButtonGroup
                        sx={{
                            display: caseHasDocuments ? "inline-flex" : "none",
                            pointerEvents: "auto",
                            ml: 2,
                            mr: 2
                        }}
                        orientation="vertical"
                        aria-label="vertical contained button group"
                        variant="text"
                        className="selectDocumentButtonGroup"
                        size="small"
                    >
                        <Button
                            disabled={Object.keys(refFileNamesToFilePropertiesMap.current)
                                .filter(filename => refFileNamesToFilePropertiesMap.current[filename]['selected']).length === Object.keys(refFileNamesToFilePropertiesMap.current).length}
                            onClick={(event) => {
                                event.stopPropagation();
                                var newFileNamesToFilePropertiesMap = { ...refFileNamesToFilePropertiesMap.current };
                                Object.keys(newFileNamesToFilePropertiesMap).forEach(filename => {
                                    newFileNamesToFilePropertiesMap[filename]['selected'] = true;
                                })
                                setFileNamesToFilePropertiesMap(newFileNamesToFilePropertiesMap);
                            }}
                            sx={{
                                whiteSpace: 'nowrap'
                            }}
                            key="one"
                            size="small">Select All</Button>
                        <Button disabled={Object.keys(refFileNamesToFilePropertiesMap.current)
                            .filter(filename => refFileNamesToFilePropertiesMap.current[filename]['selected']).length === 0}
                            onClick={(event) => {
                                event.stopPropagation();
                                var newFileNamesToFilePropertiesMap = { ...refFileNamesToFilePropertiesMap.current };
                                Object.keys(newFileNamesToFilePropertiesMap).forEach(filename => {
                                    newFileNamesToFilePropertiesMap[filename]['selected'] = false;
                                })
                                setFileNamesToFilePropertiesMap(newFileNamesToFilePropertiesMap);
                            }}
                            sx={{
                                whiteSpace: 'nowrap'
                            }}
                            key="two"
                            size="small">Select None</Button>
                    </ButtonGroup>
                    <div style={{ marginLeft: "auto", display: "flex", alignItems: "center" }}
                        onClick={(e) => { e.stopPropagation() }}
                    >
                        <Autocomplete
                            style={{
                                visibility: (caseInfo['case_type'] && caseHasDocuments) ? "visible" : "hidden",
                                pointerEvents: "auto"
                            }}
                            value={caseInfo['case_type'] || null}
                            id="case_type"
                            // autoComplete={true} // not working
                            // autoHighlight={true} // not working
                            autoSelect={true}
                            onChange={onCaseTypeChanged}
                            options={caseTypes}
                            blurOnSelect={true}
                            onBlur={onCaseTypeBlur}
                            clearOnEscape={false}
                            selectOnFocus={true}
                            handleHomeEndKeys={true}
                            freeSolo={true}
                            sx={{
                                width: { md: 200 },
                                minWidth: 150,
                                maxWidth: 400,
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '8px',
                                    '& fieldset': {
                                        borderColor: 'var(--border-color)',
                                    },
                                    '&:hover fieldset': {
                                        borderColor: 'var(--accent-color)',
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: 'var(--accent-color)',
                                    },
                                }
                            }}
                            renderInput={(params) => <TextField {...params} label="Case Type" />}
                        />
                    </div>
                </AccordionSummary>
                <AccordionDetails style={{ display: "block", padding: '16px' }}>
                    <Box
                        display={caseInfo['case_type'] && caseHasDocuments ? "none" : "flex"}
                        sx={{
                            margin: "24px 0 20px 0",
                            flexDirection: "row",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                            <Box sx={{ marginRight: "16px", flexShrink: 0 }}>
                                <Typography
                                    variant={"h6"}
                                    sx={{
                                        fontWeight: caseInfo['case_type'] ? 400 : 600,
                                        color: 'var(--text-primary)',
                                        mb: 0.5
                                    }}
                                >
                                    Enter Case Type
                                </Typography>
                                <Typography
                                    variant="div"
                                    sx={{
                                        fontWeight: 400,
                                        fontSize: "0.8rem",
                                        color: 'var(--text-secondary)'
                                    }}
                                >
                                    eg: Car Accident, Dog bite, etc
                                </Typography>
                            </Box>
                            <Autocomplete
                                value={caseInfo['case_type'] || ''}
                                id="case_type"
                                // autoComplete={true} // not working
                                // autoHighlight={true} // Click on manually entered value and hitting enter causes first option to be selected
                                autoSelect={true}
                                onChange={onCaseTypeChanged}
                                options={caseTypes}
                                blurOnSelect={true}
                                onBlur={onCaseTypeBlur}
                                clearOnEscape={false}
                                selectOnFocus={true}
                                handleHomeEndKeys={true}
                                freeSolo={true}
                                sx={{
                                    width: 400,
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '8px',
                                        '& fieldset': {
                                            borderColor: showCaseTypeWarning ? 'error.main' : 'var(--border-color)',
                                            borderWidth: showCaseTypeWarning ? 2 : 1,
                                        },
                                        '&:hover fieldset': {
                                            borderColor: showCaseTypeWarning ? 'error.main' : 'var(--accent-color)',
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: showCaseTypeWarning ? 'error.main' : 'var(--accent-color)',
                                        },
                                    }
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        error={showCaseTypeWarning}
                                        helperText={showCaseTypeWarning ? 'Enter case type for accurate processing' : undefined}
                                        FormHelperTextProps={{
                                            sx: { margin: 0, mt: 0.5 },
                                        }}
                                    />
                                )}
                            />
                        </Box>
                    </Box>
                    {caseHasDocuments && renderDocumentChoicesPanel()}
                    <div style={{ margin: caseHasDocuments ? "20px 0 0 0" : "40px 0 0 0" }}>
                        <Typography
                            variant={"h6"}
                            sx={{
                                fontWeight: caseInfo['case_type'] ? 600 : 500,
                                color: 'var(--text-primary)',
                                mb: 1.5
                            }}
                        >
                            Upload Case Documents
                        </Typography>
                        <Typography
                            sx={{
                                fontSize: "0.95rem",
                                fontWeight: 400,
                                color: 'var(--text-secondary)',
                                mb: 2
                            }}
                            display={caseHasDocuments ? "none" : "inherit"}
                        >
                            Medical records, medical bills, police reports, client intake, etc.
                        </Typography>
                    </div>
                    <div className="file-uploader-wrapper">
                        <Box sx={{
                            position: 'relative',
                            opacity: subscriptionOk ? 1 : 0.45,
                            transition: 'opacity 0.2s ease',
                        }}>
                            {!subscriptionOk && (
                                <Box
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        onSubscriptionRequired?.();
                                    }}
                                    onDragOver={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                    }}
                                    onDrop={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        onSubscriptionRequired?.();
                                    }}
                                    sx={{
                                        position: 'absolute',
                                        inset: 0,
                                        zIndex: 2,
                                        cursor: 'pointer',
                                    }}
                                />
                            )}
                            <FileUploader
                                handleChange={onFilesSelectedForUpload}
                                hoverTitle="Drop for secure upload"
                                name="file"
                                types={["PDF", "TXT"]} //, "DOCX", "JPG", "PNG", "GIF"
                                multiple={true}
                                dropMessageStyle={{
                                    background: 'var(--background-color)',
                                    border: '2px dashed var(--border-hover)',
                                    borderRadius: '12px',
                                    boxShadow: 'var(--shadow-light)',
                                    color: 'var(--text-primary)',
                                    transform: 'translateY(-1px)',
                                    fontWeight: 500
                                }}
                            >
                                <Box sx={{
                                    border: '2px dashed var(--border-color)',
                                    borderRadius: '12px',
                                    cursor: "pointer",
                                    padding: caseHasDocuments ? "10px" : "40px",
                                    transition: 'all 0.2s ease',
                                    width: '100%',
                                    '&:hover': {
                                        borderColor: 'var(--border-hover) !important',
                                        boxShadow: 'var(--shadow-light)',
                                        transform: 'translateY(-2px)'
                                    }
                                }}>
                                    <img
                                        style={{
                                            height: "50px",
                                            opacity: 0.8,
                                        }}
                                        src="/upload.png"
                                        alt="upload"
                                    />
                                    <Box sx={{
                                        display: "inline-flex",
                                        flexDirection: "column",
                                        verticalAlign: "top",
                                        alignItems: 'center'
                                    }}>
                                        <Typography
                                            variant="h6"
                                            style={{
                                                display: "inline",
                                                color: 'var(--text-primary)',
                                                fontWeight: 500,
                                                marginBottom: '2px'
                                            }}
                                        >
                                            Secure Upload
                                        </Typography>
                                        <Typography
                                            variant="div"
                                            style={{
                                                display: "inline",
                                                color: 'var(--text-secondary)',
                                                fontSize: '0.85rem'
                                            }}
                                        >
                                            Click or drop files to upload
                                        </Typography>
                                    </Box>
                                </Box>
                            </FileUploader>
                        </Box>
                    </div>
                </AccordionDetails>
            </Accordion>
        </div >
    );
    async function onCaseNameChanged(changeObj) {
        var newCaseName = changeObj.value;
        var newCaseInfo = {
            ...caseInfo,
            name: newCaseName
        };
        setCaseInfo(newCaseInfo);

        let response;
        try {
            response = await fetch('/api/case/' + caseInfo['caseId'], {
                method: 'POST',
                credentials: 'same-origin',
                headers: new Headers({ 'content-type': 'application/json' }),
                body: JSON.stringify({ name: newCaseName }),
            });
        } catch (e) {
            console.log('Case name change failed: ' + e);
            reportError(e, 'CaseSummaryHeader.onCaseNameChanged', {
                caseId: caseInfo?.['caseId'],
                newCaseName
            });
            setSnackBarMessage('ERROR: Failed to rename case. Please try again');
            return;
        }
        if (response?.ok) {
            setSnackBarMessage('Case renamed to ' + newCaseName);
        } else {
            const error = new Error(`Case rename failed with status: ${response?.status}`);
            reportError(error, 'CaseSummaryHeader.onCaseNameChanged', {
                caseId: caseInfo?.['caseId'],
                newCaseName,
                status: response?.status
            });
            setSnackBarMessage('ERROR: Failed to rename case. Please try again');
        }
        console.log('onCaseNameChanged response' + JSON.stringify(response));
    }

    async function onCaseTypeChanged(event, newInputValue) {
        if (newInputValue === null) {
            console.log('Null case type. Returning');
            return;
        }
        console.log('onCaseTypeChanged newInputValue: ' + JSON.stringify(newInputValue));

        if (newInputValue === caseInfo['case_type']) {
            console.log('Same case type as before; returning:' + newInputValue);
            return;
        }
        var newCaseType = newInputValue ? newInputValue['label'] || newInputValue : '';

        var newCaseInfo = {
            ...caseInfo,
            case_type: newCaseType
        };
        setCaseInfo(newCaseInfo);

        let response;
        try {
            response = await fetch('/api/case/' + caseInfo['caseId'], {
                method: 'POST',
                credentials: 'same-origin',
                headers: new Headers({ 'content-type': 'application/json' }),
                body: JSON.stringify({ case_type: newCaseType }),
            });
        } catch (e) {
            console.log('Case type change failed: ' + e);
            reportError(e, 'CaseSummaryHeader.onCaseTypeChanged', {
                caseId: caseInfo?.['caseId'],
                newCaseType
            });
            setSnackBarMessage('ERROR: Failed to set case type. Please try again');
            return;
        }
        if (response?.ok) {
            if (newCaseType) {
                setSnackBarMessage('Case type set to ' + newCaseType);
            } else {
                setSnackBarMessage('Case type removed');
            }
        } else {
            const error = new Error(`Case type change failed with status: ${response?.status}`);
            reportError(error, 'CaseSummaryHeader.onCaseTypeChanged', {
                caseId: caseInfo?.['caseId'],
                newCaseType,
                status: response?.status
            });
            setSnackBarMessage('ERROR: Failed to set case type. Please try again');
        }
        console.log('onCaseTypeChanged response' + response);
    }

    /**
     * Handles case when user enters text and clicks outside. Default autocomplete behavior is to ignore 
     * this value and not trigger a onChange. To keep behavior consistency with EditText, we manually trigger
     * onCaseTypeChanged.
     * @param event blurEvent
     */
    async function onCaseTypeBlur(event) {
        try {
            // Add delay to handle case when a few letters are typed and enter is pressed to select 
            // autocompleted entry. In this case at the time of blur, the selected value is not
            // yet populated in the input.
            setTimeout(() => onCaseTypeChanged(event, event.target.value), 10);
        } catch (error) {
            console.error('[CaseSummaryHeader] Error in onCaseTypeBlur:', error);
            reportError(error, 'CaseSummaryHeader.onCaseTypeBlur', {
                caseId: caseInfo?.['caseId']
            });
        }
    }

    function renderDocumentChoicesToggleLink(label, onClick, isExpanded) {
        return (
            <Divider sx={{ mb: 3 }}>
                <Button
                    size="small"
                    variant="text"
                    endIcon={isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    onClick={onClick}
                    sx={{ textTransform: 'none', fontWeight: 600 }}
                >
                    {label}
                </Button>
            </Divider>
        );
    }

    function renderDocumentChoicesPanel() {
        if (documentChoicesView === 'hidden') {
            return renderDocumentChoicesToggleLink(
                'Show document choices for inclusion/exclusion',
                () => {
                    trackDocumentChoicesPanelClick('show');
                    setDocumentChoicesView('full');
                },
                false
            );
        }

        const isPartialView = useCollapsibleDocumentPanel && documentChoicesView === 'partial';

        return (
            <>
                <Box
                    sx={{
                        maxHeight: isPartialView ? `${DOCUMENT_CHOICES_PARTIAL_HEIGHT}px` : 'none',
                        overflow: 'hidden',
                    }}
                >
                    <Grid container spacing={2} columns={4}>
                        {gridItems}
                    </Grid>
                </Box>
                {useCollapsibleDocumentPanel && (
                    isPartialView
                        ? renderDocumentChoicesToggleLink(
                            'Show all document choices for inclusion/exclusion',
                            () => {
                                trackDocumentChoicesPanelClick('show_all');
                                setDocumentChoicesView('full');
                            },
                            false
                        )
                        : renderDocumentChoicesToggleLink(
                            'Hide document choices',
                            () => {
                                trackDocumentChoicesPanelClick('hide');
                                setDocumentChoicesView('hidden');
                            },
                            true
                        )
                )}
                {!useCollapsibleDocumentPanel && <Box sx={{ mb: 3 }} />}
            </>
        );
    }

    function renderFileTogglesForCategory(category, categoryToSourceFilekeysMap, fileNamesToFilePropertiesMap) {
        // console.log('renderFileTogglesForCategory category' + category
        //     + '; categoryToSourceFilenamesMap: ' + JSON.stringify(categoryToSourceFilenamesMap)
        //     + ';\nrefFileNamesToFilePropertiesMap: ' + JSON.stringify(refFileNamesToFilePropertiesMap));

        const toggleButtons = categoryToSourceFilekeysMap[category].map(filekey => {
            const filename = caseInfo['documents'][filekey]['filename'];
            // console.log('filekey: ' + filekey + '\n fileNamesToFilePropertiesMap[filekey]: ' + JSON.stringify(fileNamesToFilePropertiesMap[filekey]));
            return (<ToggleButton sx={{
                margin: 1,
                width: "90%",
                fontSize: '0.75em',
                '&:hover': {
                    transform: 'translateY(-1px)',
                    boxShadow: 'var(--shadow-subtle)',
                    background: 'var(--surface-hover)',
                }
            }}
                type='button'
                onChange={(event, value) => {
                    var newFileNamesToFilePropertiesMap = { ...fileNamesToFilePropertiesMap };
                    const newSelectedValue = !newFileNamesToFilePropertiesMap[filekey]['selected'];
                    newFileNamesToFilePropertiesMap[filekey]['selected'] = newSelectedValue;
                    setFileNamesToFilePropertiesMap(newFileNamesToFilePropertiesMap);
                    trackDocumentToggle({ category, included: newSelectedValue });
                    setSnackBarMessage(newSelectedValue ?
                        'File selected. Information from this file included below.' :
                        'File deselected. Information from this file removed below.');
                }}
                value={filekey}
                selected={fileNamesToFilePropertiesMap[filename]['selected']}
                // aria-label="bold"
                key={filekey}
                title={fileNamesToFilePropertiesMap[filename]['status']?.includes('error') ? "Error processing document. You can retry by uploading again. If the problem persists, please report it using the contact link." : fileNamesToFilePropertiesMap[filename]['selected'] ? "Click to deselect and exclude this document from display below." : "Click to select and include this document from display below."}
                style={{ backgroundColor: fileNamesToFilePropertiesMap[filename]['selected'] ? "var(--selected-bg)" : 'var(--surface-color)' }}

            // style={{ backgroundColor: fileNamesToFilePropertiesMap[filename]['selected'] ? fileNamesToFilePropertiesMap[filename]['color'] : '#EEE' }}
            >
                <img src="/loading.gif" alt="loading..." title="Processing...(~10 seconds per page)" style={{ marginRight: "5px", height: "20px", display: fileNamesToFilePropertiesMap[filename]['status']?.includes('processing') ? "initial" : "none" }} />
                <img src="/error.png" alt="error" title="Error processing document. You can retry by uploading again. If the problem persists, please report it using the contact link." style={{ marginRight: "5px", height: "20px", display: fileNamesToFilePropertiesMap[filename]['status']?.includes('error') ? "initial" : "none" }} />
                {filename.substring(0, 30)} {filename.length > 30 ? '...' : ''}
            </ToggleButton >)
        });
        return (
            <div>
                {/* <EventIcon eventCategory={category}></EventIcon> */}
                <Typography variant="h6">{category}</Typography>
                {toggleButtons}
            </div>);
    }
}